import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Snackbar,
  IconButton,
  Stack,
  LinearProgress,
  Alert,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import EditIcon from "@mui/icons-material/Edit";
import { DataGrid } from "@mui/x-data-grid";
import * as XLSX from "xlsx";
import { db } from "../../../config/firebase";
import {
  collection,
  getDocs,
  addDoc,
  setDoc,
  doc,
  deleteDoc,
} from "firebase/firestore";
import TechPrepPortalMgtBreadCrumbs from "../../Layout/TechPrepPortalMgtBreadCrumbs";
import ClearCollectionButton from "../../common/ClearCollectionButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileDownload } from "@fortawesome/free-solid-svg-icons";
import { faFileImport } from "@fortawesome/free-solid-svg-icons";
import { faArrowRotateLeft } from "@fortawesome/free-solid-svg-icons";
import { faFileExcel } from "@fortawesome/pro-solid-svg-icons";
import { downloadExcelTemplate } from "../../../utils/exportTemplate";
import ModalDialogTitle from "../../common/ModalDialogTitle";
import ModalActions from "../../common/ModalActions";
import ConfirmDeleteDialog from "../../common/ConfirmDeleteDialog";

// Boolean display helper (same logic as used elsewhere for -1/0, etc.)
const getBooleanDisplayValue = (value) => {
  if (value === null || typeof value === "undefined") return "";

  const str = String(value).trim().toLowerCase();

  if (str === "0" || str === "0.0") return "No";
  if (str === "-1" || str === "-1.0" || str === "1" || str === "1.0")
    return "Yes";

  if (str === "y" || str === "yes") return "Yes";
  if (str === "n" || str === "no") return "No";

  return String(value);
};

const initialForm = {
  participation_status: "",
  active: "",
};

const ShowcaseParticipationStatus = () => {
  const [data, setData] = useState([]);
  const [filter, setFilter] = useState({
    participation_status: "",
    Active: "", // Default to show all
  });
  const [filteredData, setFilteredData] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [importStatus, setImportStatus] = useState({
    open: false,
    added: 0,
    updated: 0,
    total: 0,
    loading: false,
  });
  const [rowSelectionModel, setRowSelectionModel] = useState([]);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [confirmCloseOpen, setConfirmCloseOpen] = useState(false);

  const COLLECTION_NAME = "ShowcaseParticipationStatus";

  useEffect(() => {
    const fetchData = async () => {
      const snapshot = await getDocs(collection(db, COLLECTION_NAME));
      let rows = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Sort by participation_status alphabetically
      rows = rows.sort((a, b) =>
        (a.participation_status || "").localeCompare(
          b.participation_status || "",
        ),
      );
      setData(rows);
    };
    fetchData();
  }, []);

  useEffect(() => {
    let filtered = data.filter((row) =>
      Object.keys(filter).every((key) => {
        if (key === "Active") {
          if (filter.Active === "-1" || filter.Active === "0") {
            return String(row.Active) === filter.Active;
          }
          return true;
        }
        return !filter[key]
          ? true
          : (row[key] || "")
              .toString()
              .toLowerCase()
              .includes(filter[key].toString().toLowerCase());
      }),
    );
    setFilteredData(filtered);
  }, [data, filter]);

  const handleOpenAdd = () => {
    setEditIndex(null);
    setForm(initialForm);
    setModalOpen(true);
  };

  const handleOpenEdit = (params) => {
    const idx = data.findIndex((d) => d.id === params.row.id);
    setEditIndex(idx);
    setForm(params.row);
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  // Only ask for confirmation when editing an existing record — a fresh
  // Add form has nothing at risk yet. The X (and backdrop/Escape, which
  // share this same handler) go through the same check.
  const handleCloseAttempt = () => {
    if (editIndex !== null) {
      setConfirmCloseOpen(true);
    } else {
      handleCloseModal();
    }
  };

  const handleCancelClose = () => {
    setConfirmCloseOpen(false);
  };

  const handleConfirmClose = () => {
    setConfirmCloseOpen(false);
    handleCloseModal();
  };

  const handleFormChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    if (!form.participation_status) {
      setSnackbar({
        open: true,
        message: "Participation Status is required.",
        severity: "error",
      });
      return;
    }

    try {
      if (editIndex !== null) {
        const docId = data[editIndex].id;
        await setDoc(doc(db, COLLECTION_NAME, docId), form);
        const newData = [...data];
        newData[editIndex] = { ...form, id: docId };
        setData(newData);
        setSnackbar({
          open: true,
          message: "Showcase participation status updated",
          severity: "success",
        });
      } else {
        const docRef = await addDoc(collection(db, COLLECTION_NAME), form);
        setData([...data, { ...form, id: docRef.id }]);
        setSnackbar({
          open: true,
          message: "Showcase participation status added",
          severity: "success",
        });
      }
      setModalOpen(false);
    } catch (err) {
      setSnackbar({
        open: true,
        message: "Error saving to Firestore",
        severity: "error",
      });
    }
  };

  const handleDelete = async () => {
    if (editIndex !== null) {
      const docId = data[editIndex].id;
      await deleteDoc(doc(db, COLLECTION_NAME, docId));
      const newData = data.filter((_, idx) => idx !== editIndex);
      setData(newData);
      setModalOpen(false);
      setSnackbar({
        open: true,
        message: "Showcase participation status deleted",
        severity: "success",
      });
    }
  };

  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImportStatus({
      open: true,
      added: 0,
      updated: 0,
      total: 0,
      loading: true,
    });
    const reader = new FileReader();

    const processExcelRow = async (row, merged, counts) => {
      const { added, updated } = counts;
      const idx = merged.findIndex(
        (d) => d.participation_status === row.participation_status,
      );
      if (idx > -1) {
        const docId = merged[idx].id;
        await setDoc(doc(db, COLLECTION_NAME, docId), row);
        merged[idx] = { ...merged[idx], ...row };
        counts.updated = updated + 1;
      } else {
        const docRef = await addDoc(collection(db, COLLECTION_NAME), row);
        merged.push({ ...row, id: docRef.id });
        counts.added = added + 1;
      }
    };

    reader.onload = async (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: "binary" });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const jsonData = XLSX.utils.sheet_to_json(ws);

      const merged = [...data];
      const counts = { added: 0, updated: 0 };

      for (const row of jsonData) {
        await processExcelRow(row, merged, counts);
        setImportStatus((s) => ({
          ...s,
          total: jsonData.length,
          added: counts.added,
          updated: counts.updated,
        }));
      }

      setData(merged);
      setSnackbar({
        open: true,
        message: "Excel data imported",
        severity: "success",
      });
      setImportStatus((s) => ({ ...s, loading: false }));
    };

    reader.readAsBinaryString(file);
  };

  const handleDeleteMultipleConfirmed = async () => {
    for (const id of rowSelectionModel) {
      await deleteDoc(doc(db, COLLECTION_NAME, id));
    }
    setData(data.filter((row) => !rowSelectionModel.includes(row.id)));
    setRowSelectionModel([]);
    setConfirmDeleteOpen(false);
    setSnackbar({
      open: true,
      message: "Selected participation statuses deleted",
      severity: "success",
    });
  };

  const handleOpenConfirmDelete = () => {
    if (!rowSelectionModel || rowSelectionModel.length === 0) return;
    setConfirmDeleteOpen(true);
  };

  // Export actual data to Excel
  const handleExportExcel = () => {
    if (!filteredData.length) {
      setSnackbar({
        open: true,
        message: "No data to export",
        severity: "warning",
      });
      return;
    }
    const exportData = filteredData.map(({ id, ...row }) => row); // Remove id field
    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(
      workbook,
      worksheet,
      "ShowcaseParticipationStatus",
    );
    XLSX.writeFile(workbook, "showcase-participation-status.xlsx");
  };

  const handleExportTemplate = () => {
    downloadExcelTemplate(
      Object.keys(initialForm),
      `${COLLECTION_NAME}-template`,
    );
  };

  const rowsForGrid = filteredData.map((row) => ({
    ...row,
    id: String(row.id),
  }));

  const columns = [
    {
      field: "participation_status",
      headerName: "Participation Status",
      flex: 1,
      sortable: true,
    },
    {
      field: "active",
      headerName: "Active",
      flex: 0.5,
      sortable: true,
      renderCell: (params) =>
        params?.value !== undefined ? getBooleanDisplayValue(params.value) : "",
      valueFormatter: (value) =>
        value !== undefined && value !== null ? getBooleanDisplayValue(value) : "",
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <IconButton
          color="primary"
          onClick={() => handleOpenEdit(params)}
          size="small"
        >
          <EditIcon />
        </IconButton>
      ),
      width: 90,
    },
  ];

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" gutterBottom sx={{ mb: 1 }}>
        Showcase Participation Status Management
      </Typography>
      <TechPrepPortalMgtBreadCrumbs pageName="Showcase Status" />
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        mb={2}
        alignItems="center"
        sx={{ mt: 3, mb: 3 }}
      >
        <TextField
          label="Filter Participation Status"
          name="participation_status"
          value={filter.participation_status}
          onChange={(e) =>
            setFilter({ ...filter, participation_status: e.target.value })
          }
          size="small"
        />
        {/* Replace Filter Active text field with radio buttons */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography variant="body2">Active:</Typography>
          <Button
            variant={filter.Active === "" ? "contained" : "outlined"}
            size="small"
            onClick={() => setFilter({ ...filter, Active: "" })}
          >
            All
          </Button>
          <Button
            variant={filter.Active === "-1" ? "contained" : "outlined"}
            size="small"
            onClick={() => setFilter({ ...filter, Active: "-1" })}
          >
            Yes
          </Button>
          <Button
            variant={filter.Active === "0" ? "contained" : "outlined"}
            size="small"
            onClick={() => setFilter({ ...filter, Active: "0" })}
          >
            No
          </Button>
        </Stack>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenAdd}
        >
          Add
        </Button>
        <IconButton component="label" size="medium">
          <FontAwesomeIcon icon={faFileImport} color="#2e7d32" />
          <input
            type="file"
            accept=".xlsx, .xls"
            hidden
            onChange={handleExcelUpload}
          />
        </IconButton>
        {/* Only one export icon, blue, for exporting actual data */}
        <IconButton onClick={handleExportExcel} size="medium">
          <FontAwesomeIcon icon={faFileDownload} color="#1976d2" />
        </IconButton>
        <Button
          variant="contained"
          color="success"
          startIcon={<FontAwesomeIcon icon={faFileExcel} />}
          sx={{ color: "white" }}
          onClick={handleExportTemplate}
        >
          Export Template
        </Button>
        {rowSelectionModel && rowSelectionModel.length > 0 && (
          <Button
            variant="outlined"
            color="error"
            onClick={handleOpenConfirmDelete}
            sx={{ ml: 1 }}
          >
            Delete Selected
          </Button>
        )}
        <ClearCollectionButton collectionName="ShowcaseParticipationStatus" />
      </Stack>
      <Box
        sx={{
          height: 500,
          width: "100%",
          background: "#fff",
          borderRadius: 2,
          boxShadow: 1,
        }}
      >
        <DataGrid
          rows={rowsForGrid}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 20, 50]}
          disableSelectionOnClick
          onRowDoubleClick={handleOpenEdit}
          checkboxSelection
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={(newSelectionModel) => {
            setRowSelectionModel(newSelectionModel);
          }}
        />
      </Box>
      <Dialog
        open={modalOpen}
        onClose={handleCloseAttempt}
        maxWidth="sm"
        fullWidth
      >
        <ModalDialogTitle onClose={handleCloseAttempt}>
          {editIndex !== null
            ? "Edit Showcase Participation Status"
            : "Add Showcase Participation Status"}
        </ModalDialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label="Participation Status"
              name="participation_status"
              value={form.participation_status}
              onChange={handleFormChange}
              required
              fullWidth
            />
            {/* Replace Active text field with Yes/No radio buttons */}
            <FormLabel component="legend">Active</FormLabel>
            <RadioGroup
              row
              name="active"
              value={form.active === undefined ? "-1" : String(form.active)}
              onChange={handleFormChange}
            >
              <FormControlLabel value="-1" control={<Radio />} label="Yes" />
              <FormControlLabel value="0" control={<Radio />} label="No" />
            </RadioGroup>
          </Stack>
        </DialogContent>
        <ModalActions
          onSave={handleSave}
          onDelete={editIndex !== null ? handleDelete : undefined}
        />
      </Dialog>
      <ConfirmDeleteDialog
        open={confirmDeleteOpen}
        onClose={() => setConfirmDeleteOpen(false)}
        onConfirm={handleDeleteMultipleConfirmed}
        title="Delete Selected Participation Statuses"
        message={`Are you sure you want to delete ${
          rowSelectionModel.length
        } selected record${
          rowSelectionModel.length === 1 ? "" : "s"
        }? This action cannot be undone.`}
      />
      <Dialog
        open={confirmCloseOpen}
        onClose={handleCancelClose}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ pt: 3 }}>
          <Typography>
            Are you sure you want to close before saving your changes?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCancelClose}>No</Button>
          <Button onClick={handleConfirmClose} variant="contained" color="error">
            Yes, Close
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        message={snackbar.message}
      />
      {importStatus.open && (
        <Box sx={{ mt: 2 }}>
          {importStatus.loading && <LinearProgress />}
          <Alert severity="info" sx={{ mt: 1 }}>
            Importing Excel:{" "}
            {importStatus.loading ? "Processing..." : "Complete."} <br />
            Added: {importStatus.added} &nbsp; Updated: {importStatus.updated}{" "}
            &nbsp; Total: {importStatus.total}
          </Alert>
        </Box>
      )}
    </Box>
  );
};

export default ShowcaseParticipationStatus;
